import * as React from 'react';

import { translate } from '~l10n/client';

import { Checkbox, CheckboxChecked } from '~shared/components/Checkbox';
import { Divider } from '~shared/components/Divider';
import { TableSortButton } from '~shared/components/TableSortButton';
import { Direction } from '~shared/utils/sort';
import {
  DispatchColumnSortAction,
  QuizListColumn,
} from '~teacher/components/quizzes/QuizList/useColumnSortReducer';

export interface QuizListProps {
  isDeleted?: boolean;
  children: React.ReactNode;
  selectedQuizItem: Set<string>;
  selectedAll: CheckboxChecked;
  onChangeSelect: () => void;
  showOwner?: boolean;
  actions?: React.ReactNode;
  sortProperty: QuizListColumn;
  sortDirection: Direction;
  dispatchColumnSortAction: DispatchColumnSortAction;
}

export function QuizList({
  children,
  isDeleted = false,
  selectedQuizItem,
  selectedAll,
  onChangeSelect,
  showOwner,
  actions,
  sortProperty,
  sortDirection,
  dispatchColumnSortAction,
}: QuizListProps) {
  const onTableSortClick = (column: QuizListColumn) => () =>
    dispatchColumnSortAction({
      type: 'ToggleColumn',
      quizListColumn: column,
    });

  const getSortDirection = (column: QuizListColumn) =>
    sortProperty === column ? sortDirection : undefined;

  const showActions = selectedQuizItem?.size >= 1;

  /**
   * Table is constructed with `div`s and aria-roles to avoid a bug with Safari
   * where `position:relative` can't be applied to `tr` elements.
   */
  return (
    <div role="table" data-table className="w-full table-fixed border-collapse">
      <div role="rowgroup" data-thead className="sticky top-0 z-20">
        <div role="row" data-tr className="flex items-center">
          <div role="columnheader" data-th className="w-16 p-0">
            <div className="flex justify-center py-5 pl-2">
              <Checkbox
                size="large"
                id="select-all"
                checked={selectedAll}
                onChange={onChangeSelect}
              />
            </div>
          </div>
          <div
            data-th
            className="flex-1 w-full p-0 text-left"
            role="columnheader"
            aria-sort={getSortDirection(QuizListColumn.Title)}
          >
            <div className="absolute inset-0 -z-1 bg-white" />
            <div className="absolute right-0 bottom-0 left-0 z-0">
              <Divider />
            </div>
            <div className="absolute top-0 right-0 left-0 z-0">
              <Divider />
            </div>
            {/* Hide sort buttons when displaying bulk actions. */}
            {showActions ? (
              <div className="pl-4">{actions}</div>
            ) : (
              <TableSortButton
                onClick={onTableSortClick(QuizListColumn.Title)}
                sortDirection={getSortDirection(QuizListColumn.Title)}
              >
                {translate('Name')}
              </TableSortButton>
            )}
          </div>
          {showOwner ? (
            <div
              data-th
              className="hidden md:block w-56 p-0 text-left"
              role="columnheader"
              aria-sort={getSortDirection(QuizListColumn.Owner)}
            >
              <TableSortButton
                onClick={onTableSortClick(QuizListColumn.Owner)}
                sortDirection={getSortDirection(QuizListColumn.Owner)}
              >
                {translate('Owner')}
              </TableSortButton>
            </div>
          ) : null}
          <div
            data-th
            className="hidden md:block w-52 p-0 text-left"
            role="columnheader"
            aria-sort={getSortDirection(QuizListColumn.Modified)}
          >
            <TableSortButton
              onClick={onTableSortClick(QuizListColumn.Modified)}
              sortDirection={getSortDirection(QuizListColumn.Modified)}
            >
              {isDeleted ? translate('Deleted') : translate('Modified')}
            </TableSortButton>
          </div>
          <div role="cell" data-td className="w-12 p-0"></div>
        </div>
      </div>
      <div role="rowgroup" data-tbody>
        {children}
      </div>
    </div>
  );
}
